// A locked cover's video, as a preview of the format: the studio's Spotlight
// template drawn live (lib/videostudio's drawFrame, the same one the studio
// exports with) with the cover's voice and song name, blurred behind a lock.
// Nothing has been made yet - a locked cover never runs - so there are no
// vocals or lyrics in it, and the label says it's what the video will look
// like once the cover is made, not that it exists.
import React, { useEffect, useRef, useState } from "react";
import { Lock } from "lucide-react";
import { drawFrame, type Scene, type Singer } from "@/lib/videostudio/frame";
import { newProject } from "@/lib/videostudio/project";
import { ensureFonts } from "@/lib/videostudio/fonts";
import { useT } from "@jammable/ui-core/src/i18n";

/* eslint-disable @typescript-eslint/no-explicit-any */

// Where LockedCover's "Matching the voice" step starts (its STAGE_ENDS[0]).
const VOICE_STEP = 0.17;

const W = 360;
const H = 640;
const LOOP_SECS = 30;
const COLORS = ["#782DC8", "#D1FE17", "#FF4D8D", "#3DE8FF"];

/** A voice's photo, through our image proxy so the canvas can read it. */
async function photoOf(url?: string | null): Promise<ImageBitmap | null> {
  if (!url) return null;
  try {
    const res = await fetch(`/api/studio/image?url=${encodeURIComponent(url)}`);
    if (!res.ok) return null;
    return await createImageBitmap(await res.blob());
  } catch {
    return null;
  }
}

// The frame, in a 90x160 box (it's always 9:16): its corners, and the
// progress ring that runs round its border from the top centre, clockwise.
// A path rather than a rect with pathLength: Safari splits the dash where a
// rect starts. The frame's corners use the same proportions so they line up.
const RADIUS = 7.6,
  RW = 90,
  RH = 160,
  INSET = 1.2;
const RING = (() => {
  const r = RADIUS - INSET,
    R = RW - INSET,
    B = RH - INSET;
  return [
    `M ${RW / 2} ${INSET}`,
    `H ${R - r} A ${r} ${r} 0 0 1 ${R} ${INSET + r}`,
    `V ${B - r} A ${r} ${r} 0 0 1 ${R - r} ${B}`,
    `H ${INSET + r} A ${r} ${r} 0 0 1 ${INSET} ${B - r}`,
    `V ${INSET + r} A ${r} ${r} 0 0 1 ${INSET + r} ${INSET}`,
    `H ${RW / 2}`,
  ].join(" ");
})();
const RING_LENGTH =
  2 * (RW + RH - 4 * INSET) -
  8 * (RADIUS - INSET) +
  2 * Math.PI * (RADIUS - INSET);

export default function LockedVideoPreview({
  voices,
  title,
  onUnlock,
  cta: ctaProp,
  progress = 1,
}: {
  voices: any[];
  title: string;
  /** The whole frame is the button: the paywall, or "make it now" on a plan. */
  onUnlock: () => void;
  cta?: string;
  /**
   * The setup sequence's progress, 0..1. The video assembles with its steps:
   * the background first, then (from VOICE_STEP, "Matching the voice") the
   * voice pops in, while a ring runs round the frame; at 1 it locks.
   */
  progress?: number;
}) {
  const t = useT();
  const cta = ctaProp ?? t("paywall.unlock");
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [scene, setScene] = useState<Scene | null>(null);
  // Read by the drawing loop without restarting it.
  const progressRef = useRef(progress);
  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    let alive = true;
    Promise.all([
      ensureFonts(),
      ...voices.slice(0, 3).map((v) => photoOf(v?.image?.url)),
    ]).then(([, ...photos]) => {
      if (!alive) return;
      const singers: Singer[] = voices.slice(0, 3).map((v, i) => ({
        urlParam: v?.urlParam ?? String(i),
        name: v?.artistname ?? "",
        photo: (photos[i] as ImageBitmap | null) ?? null,
        color: COLORS[i % COLORS.length],
      }));
      const project = {
        ...newProject(LOOP_SECS),
        template: "spotlight" as const,
        format: "9:16" as const,
      };
      project.background = { ...project.background, kind: "voice" };
      setScene({
        project,
        words: [],
        voiceTimes: [],
        singers,
        backgroundImage: null,
        envelope: null,
        title,
        badge: null,
        mark: false,
      });
    });
    return () => {
      alive = false;
    };
    // The cover doesn't change under the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Plays on a loop; one still frame for reduced motion. Until the voice
  // step only the background is drawn (the voice's photo, blurred, with no
  // singer on top); then the clock starts from zero, so Spotlight's own
  // entrance (the voice popping in) plays as the voice is matched.
  useEffect(() => {
    const ctx = canvas.current?.getContext("2d");
    if (!scene || !ctx) return;
    const backdrop: Scene = {
      ...scene,
      project: {
        ...scene.project,
        template: "spotlight",
        singers: { ...scene.project.singers, on: false },
      },
    };
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (still) {
      drawFrame(ctx, W, H, scene, 3);
      return;
    }
    let raf = 0;
    const born = performance.now();
    let voiceFrom: number | null =
      progressRef.current >= VOICE_STEP ? born : null;
    const loop = (now: number) => {
      if (voiceFrom === null && progressRef.current >= VOICE_STEP)
        voiceFrom = now;
      if (voiceFrom === null) {
        drawFrame(ctx, W, H, backdrop, ((now - born) / 1000) % LOOP_SECS);
      } else {
        drawFrame(ctx, W, H, scene, ((now - voiceFrom) / 1000) % LOOP_SECS);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [scene]);

  const drawing = progress < 1;
  const p = Math.max(0, Math.min(1, progress));
  const frameClass =
    "tw-group tw-relative tw-h-[300px] tw-overflow-hidden tw-border-0 tw-bg-jam-ink tw-p-0 tw-shadow-[0_24px_60px_-24px_rgba(46,15,82,0.6)] md:tw-h-[500px]";
  const frameStyle = {
    aspectRatio: "9 / 16",
    borderRadius: `${(RADIUS / RW) * 100}% / ${(RADIUS / RH) * 100}%`,
  };
  const inside = (
    <>
      <style>{`
        @keyframes jam-lock-ping { 0% { transform: scale(1); opacity: .55; } 100% { transform: scale(1.9); opacity: 0; } }
        @keyframes jam-lock-in { from { opacity: 0; transform: translateY(8px) scale(.96); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .jam-lock-ping, .jam-lock-in { animation: none !important; } }
      `}</style>
      {/* While it sets up, the video assembles itself (see the loop). */}
      <canvas
        ref={canvas}
        width={W}
        height={H}
        className={`tw-absolute tw-inset-0 tw-h-full tw-w-full ${drawing ? "" : "tw-transition-[filter] tw-duration-500 [filter:blur(14px)_saturate(1.15)] group-hover:[filter:blur(11px)_saturate(1.2)]"}`}
        style={{
          opacity: scene ? 1 : 0,
          filter: drawing ? "blur(5px)" : undefined,
          transform: "scale(1.08)",
          transition: "opacity .6s ease-out, filter .6s ease-out",
        }}
      />
      {drawing && (
        <svg
          viewBox={`0 0 ${RW} ${RH}`}
          className="tw-pointer-events-none tw-absolute tw-inset-0 tw-h-full tw-w-full"
          aria-hidden
        >
          <path
            d={RING}
            fill="none"
            stroke="rgba(255,255,255,.18)"
            strokeWidth={2.4}
          />
          <path
            d={RING}
            fill="none"
            stroke="#D1FE17"
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeDasharray={`${p * RING_LENGTH} ${RING_LENGTH}`}
            style={{ transition: "stroke-dasharray .3s ease-out" }}
          />
        </svg>
      )}
      {!drawing && (
        <>
          {/* Even darkening, a little more at the edges, so the centre reads. */}
          <div className="tw-absolute tw-inset-0 tw-bg-jam-ink/45" />
          <div className="tw-absolute tw-inset-0 tw-bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(46,15,82,0.55)_100%)]" />
          <div
            className="jam-lock-in tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2.5 tw-px-5 tw-text-center md:tw-gap-3.5"
            style={{
              animation: "jam-lock-in .5s cubic-bezier(.2,.9,.3,1.2) both",
            }}
          >
            <span className="tw-relative tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center md:tw-h-16 md:tw-w-16">
              <span
                className="jam-lock-ping tw-absolute tw-inset-0 tw-rounded-full tw-bg-jam-lime"
                style={{ animation: "jam-lock-ping 1.8s ease-out infinite" }}
              />
              <span className="tw-relative tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink tw-shadow-[0_8px_24px_-6px_rgba(209,254,23,0.7)]">
                <Lock size={22} strokeWidth={2.75} />
              </span>
            </span>
            <span className="jam-display tw-text-[19px] tw-font-extrabold tw-leading-tight tw-text-white md:tw-text-[26px]">
              {t("paywall.video.unlock")
                .split("\n")
                .map((line, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
            </span>
            <span className="tw-text-[11px] tw-font-bold tw-text-white/75 md:tw-text-[13px]">
              {t("paywall.video.howItLooks")}
            </span>
            <span className="tw-mt-1 tw-inline-flex tw-h-10 tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-jam-lime tw-px-5 tw-text-[14px] tw-font-extrabold tw-text-jam-ink tw-transition-colors group-hover:tw-bg-[#C4EE00] md:tw-h-12 md:tw-px-6 md:tw-text-[15px]">
              <Lock size={14} strokeWidth={3} />
              {cta}
            </span>
          </div>
        </>
      )}
    </>
  );

  // One element throughout (a new one would drop the canvas the loop draws
  // to): inert while it draws, then the whole frame is the unlock button.
  return (
    <button
      type="button"
      onClick={drawing ? undefined : onUnlock}
      disabled={drawing}
      aria-label={
        drawing ? t("paywall.video.drawing") : t("paywall.video.aria", { cta })
      }
      className={`${frameClass} ${drawing ? "tw-cursor-default" : "tw-cursor-pointer tw-transition-transform tw-duration-300 hover:tw--translate-y-1"}`}
      style={frameStyle}
    >
      {inside}
    </button>
  );
}
